import { useEffect, useRef, useState } from 'react';
import { isInteractiveA2uiSurface } from '../../utils/a2ui-components';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { NavArrowDown } from 'iconoir-react';

import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import { Collapsible, CollapsibleTrigger } from 'components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { Input } from 'components/ui/input';
import { useConfig } from 'hooks/use-config';
import { useToast } from 'hooks/use-toast';
import { HttpError } from 'lib/https';
import { cn } from 'lib/utils';
import { getResumeChatStream } from 'services/sse-service';
import { useChatStore } from 'state/store/chat/use-chat-store';
import { useCommonStore } from 'state/store/common/useCommonStore';
import type { IamData } from 'features/iam/services/user-service';
import type {
  A2uiSurfaceStatus,
  ApprovalGrantScope,
  MessageA2uiApproval,
  MessageA2uiSurface,
} from 'features/chat/types/chat';
import { buildA2uiSubmit } from 'features/chat/utils/a2ui-submit';
import { resolveApprovalCardStatus } from 'features/chat/utils/approval-conversation-messages';
import { A2UISurface, A2uiAction } from './A2UISurface';

type Decision = 'approved' | 'declined';

/** Least → most permissive; also the render order of the approve split-button. */
const GRANT_SCOPE_ORDER: ApprovalGrantScope[] = ['once', 'chat', 'always_action', 'always_tool'];

/** Grant scope → resume body. `once` sends neither field (a plain approve). */
const grantPayload = (
  scope: ApprovalGrantScope
): { scope?: 'chat' | 'always'; grantTarget?: 'action' | 'tool' } => {
  switch (scope) {
    case 'chat':
      return { scope: 'chat' };
    case 'always_action':
      return { scope: 'always', grantTarget: 'action' };
    case 'always_tool':
      return { scope: 'always', grantTarget: 'tool' };
    default:
      return {};
  }
};

const grantScopeLabel = (
  t: TFunction,
  scope: ApprovalGrantScope,
  approval: MessageA2uiApproval
): string => {
  switch (scope) {
    case 'chat':
      return t('A2UI_ALLOW_FOR_THIS_CHAT', 'Allow for this chat');
    case 'always_action':
      return approval.toolName
        ? t('A2UI_ALWAYS_ALLOW_ACTION', 'Always allow {{name}}', { name: approval.toolName })
        : t('A2UI_ALWAYS_ALLOW_THIS_ACTION', 'Always allow this action');
    case 'always_tool':
      return approval.actionCount != null
        ? t('A2UI_ALWAYS_ALLOW_TOOL_ALL', 'Always allow this tool (all {{count}} actions)', {
            count: approval.actionCount,
          })
        : t('A2UI_ALWAYS_ALLOW_TOOL', 'Always allow this tool');
    default:
      return t('A2UI_ALLOW_ONCE', 'Allow once');
  }
};

const statusLabel = (t: TFunction, status: A2uiSurfaceStatus): string => {
  switch (status) {
    case 'answered':
      return t('A2UI_STATUS_ANSWERED', { defaultValue: 'Answered' });
    case 'approved':
      return t('A2UI_STATUS_APPROVED', { defaultValue: 'Approved' });
    case 'declined':
      return t('A2UI_STATUS_DECLINED', { defaultValue: 'Declined' });
    case 'expired':
      return t('A2UI_STATUS_EXPIRED', { defaultValue: 'Expired' });
    case 'skipped':
      return t('A2UI_STATUS_SKIPPED', { defaultValue: 'Skipped' });
    default:
      return '';
  }
};

const statusBadgeVariant = (
  status: A2uiSurfaceStatus
): 'default' | 'destructive' | 'secondary' => {
  if (status === 'approved') return 'default';
  if (status === 'declined') return 'destructive';
  return 'secondary';
};

const isPast = (iso?: string) => {
  const ms = iso ? Date.parse(iso) : Number.NaN;
  return !Number.isNaN(ms) && ms <= Date.now();
};

/** Client-derived display status: expired approvals, skipped stale selection surfaces. */
const deriveStatus = (surface: MessageA2uiSurface, isLatest: boolean): A2uiSurfaceStatus => {
  if (surface.status !== 'active') return surface.status;
  if (surface.approval) return isPast(surface.approval.expiresAt) ? 'expired' : 'active';
  if (!isLatest && isInteractiveA2uiSurface(surface)) return 'skipped';
  return 'active';
};

const participantName = (participants: IamData[] | undefined, userId?: string) => {
  if (!userId) return undefined;
  const participant = participants?.find((entry) => entry.itemId === userId);
  if (!participant) return undefined;
  return (
    [participant.firstName, participant.lastName].filter(Boolean).join(' ') ||
    participant.userName
  );
};

interface A2uiApprovalCardProps {
  surface: MessageA2uiSurface;
  currentUserId?: string;
  initiatorName?: string;
  onDecision: (decision: Decision, comment: string, scope?: ApprovalGrantScope) => void;
  /** Fires when the card renders (or ticks over to) an expired approval. */
  onExpired?: () => void;
  /** False when a later user message superseded this HIL interrupt. */
  isLatest?: boolean;
}

const CLOSE_MS = 320;

const PANEL_TRANSITION =
  'transition-[grid-template-rows,opacity,margin] duration-300 ease-out motion-reduce:transition-none';

/** Compact resolved approval — matches Steps chrome, not a full card. */
const ResolvedApprovalSummary = ({
  surface,
  status,
  animateIn = false,
}: {
  surface: MessageA2uiSurface;
  status: A2uiSurfaceStatus;
  animateIn?: boolean;
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [entered, setEntered] = useState(!animateIn);
  const approval = surface.approval;

  useEffect(() => {
    if (!animateIn) return;
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, [animateIn]);

  if (!approval) return null;

  const hasToolArgs = Boolean(approval.toolArgs && Object.keys(approval.toolArgs).length > 0);
  const label =
    approval.toolName ||
    t('A2UI_APPROVAL_REQUIRED', { defaultValue: 'Approval required' });

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className={cn(
        'my-1 motion-reduce:transition-none',
        animateIn
          ? 'animate-in fade-in slide-in-from-top-1 duration-300 fill-mode-both'
          : undefined,
        entered ? 'translate-y-0 opacity-100' : 'translate-y-1 opacity-0',
        !animateIn && 'transition-[opacity,transform] duration-300 ease-out'
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="group inline-flex items-center gap-1 rounded-md py-0.5 text-left transition-colors hover:bg-muted/60"
            aria-expanded={open}
          >
            <span className="text-sm font-medium text-[#6D6D6D] dark:text-muted-foreground">
              {label}
            </span>
            <NavArrowDown
              width={14}
              height={14}
              strokeWidth={2}
              className={cn(
                'shrink-0 text-[#9C9C9E] transition-transform duration-300 ease-out motion-reduce:transition-none dark:text-muted-foreground',
                open && 'rotate-180'
              )}
              aria-hidden
            />
          </button>
        </CollapsibleTrigger>
        <Badge variant={statusBadgeVariant(status)} className="h-5 px-1.5 text-[10px] font-medium">
          {statusLabel(t, status)}
        </Badge>
        {approval.subagentName && (
          <Badge
            variant="outline"
            className="h-5 px-1.5 font-normal text-[10px] text-muted-foreground"
          >
            {t('A2UI_APPROVAL_VIA', {
              defaultValue: 'via {{name}}',
              name: approval.subagentName,
            })}
          </Badge>
        )}
      </div>

      <div
        aria-hidden={!open}
        className={cn(
          'grid overflow-hidden',
          PANEL_TRANSITION,
          open ? 'mt-2 grid-rows-[1fr] opacity-100' : 'mt-0 grid-rows-[0fr] opacity-0'
        )}
      >
        <div className="min-h-0 overflow-hidden">
          {hasToolArgs && (
            <pre className="max-h-40 overflow-auto rounded-md border border-border/60 bg-muted/50 p-2 text-xs">
              {JSON.stringify(approval.toolArgs, null, 2)}
            </pre>
          )}
        </div>
      </div>
    </Collapsible>
  );
};

/** Deterministic HIL approval card (wire contract §3); only the turn initiator may act. */
const A2uiApprovalCard = ({
  surface,
  currentUserId,
  initiatorName,
  onDecision,
  onExpired,
  isLatest = true,
}: A2uiApprovalCardProps) => {
  const { t } = useTranslation();
  const [comment, setComment] = useState('');
  const [now, setNow] = useState(() => Date.now());
  // Live resolve: collapse card body, then swap to summary. History: summary immediately.
  const [phase, setPhase] = useState<'card' | 'closing' | 'summary'>(() =>
    resolveApprovalCardStatus(surface) === 'active' ? 'card' : 'summary'
  );
  const [bodyOpen, setBodyOpen] = useState(
    () => resolveApprovalCardStatus(surface) === 'active'
  );
  const [animateSummaryIn, setAnimateSummaryIn] = useState(false);
  // Stagger card sections on first appear (tool → args → actions), like Steps rows.
  const [revealStep, setRevealStep] = useState(0);

  const approval = surface.approval;
  const expiresAtMs = approval?.expiresAt ? Date.parse(approval.expiresAt) : Number.NaN;
  const hasExpiry = !Number.isNaN(expiresAtMs);
  const status = resolveApprovalCardStatus(surface, now);
  const expired = status === 'expired';
  const active = status === 'active';

  useEffect(() => {
    if (!active || !hasExpiry) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [active, hasExpiry]);

  useEffect(() => {
    if (expired) onExpired?.();
  }, [expired, onExpired]);

  useEffect(() => {
    if (active) {
      setPhase('card');
      setBodyOpen(true);
      setAnimateSummaryIn(false);
      return;
    }
    setPhase((current) => (current === 'summary' ? current : 'closing'));
    setBodyOpen(false);
  }, [active]);

  useEffect(() => {
    if (phase !== 'closing') return;
    const id = window.setTimeout(() => {
      setAnimateSummaryIn(true);
      setPhase('summary');
    }, CLOSE_MS);
    return () => window.clearTimeout(id);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'card') return;
    if (revealStep >= 3) return;
    const id = window.setTimeout(() => setRevealStep((step) => step + 1), 180);
    return () => window.clearTimeout(id);
  }, [phase, revealStep]);

  if (!approval) return null;

  if (phase === 'summary') {
    return (
      <ResolvedApprovalSummary
        surface={surface}
        status={status}
        animateIn={animateSummaryIn}
      />
    );
  }

  const isInitiator = !approval.initiatorUserId || approval.initiatorUserId === currentUserId;
  const remaining = Math.max(0, Math.floor((expiresAtMs - now) / 1000));
  const countdown = `${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, '0')}`;
  const hasToolArgs = Boolean(approval.toolArgs && Object.keys(approval.toolArgs).length > 0);
  // Empty on older backends — falls back to the plain Approve button.
  const grantScopes = GRANT_SCOPE_ORDER.filter((scope) =>
    approval.allowedScopes?.includes(scope)
  );
  const closing = phase === 'closing';
  const sectionEnter =
    'animate-in fade-in slide-in-from-top-1 duration-300 fill-mode-both motion-reduce:animate-none';

  return (
    <Card
      className={cn(
        'my-2 w-full max-w-2xl overflow-hidden transition-[opacity,transform] duration-300 ease-out motion-reduce:transition-none',
        'animate-in fade-in slide-in-from-top-2 duration-300 fill-mode-both motion-reduce:animate-none',
        closing && 'translate-y-0.5 opacity-0'
      )}
      data-surface-id={surface.surfaceId}
    >
      <CardContent className="flex flex-col p-4">
        <div className={cn('flex flex-wrap items-center justify-between gap-2', sectionEnter)}>
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-semibold">
              {t('A2UI_APPROVAL_REQUIRED', { defaultValue: 'Approval required' })}
            </p>
            {approval.subagentName && (
              <Badge variant="outline" className="font-normal text-muted-foreground">
                {t('A2UI_APPROVAL_VIA', {
                  defaultValue: 'via {{name}}',
                  name: approval.subagentName,
                })}
              </Badge>
            )}
            {closing && (
              <Badge
                variant={statusBadgeVariant(status)}
                className="h-5 px-1.5 text-[10px] font-medium"
              >
                {statusLabel(t, status)}
              </Badge>
            )}
          </div>
          {!closing && hasExpiry && (
            <span className="text-xs text-muted-foreground">
              {t('A2UI_APPROVAL_EXPIRES_IN', { defaultValue: 'Expires in' })} {countdown}
            </span>
          )}
        </div>

        <div
          aria-hidden={!bodyOpen}
          className={cn(
            'grid overflow-hidden',
            PANEL_TRANSITION,
            bodyOpen ? 'mt-3 grid-rows-[1fr] opacity-100' : 'mt-0 grid-rows-[0fr] opacity-0'
          )}
        >
          <div className="min-h-0 overflow-hidden">
            <div className="flex flex-col gap-3">
              {revealStep >= 1 && approval.toolName && (
                <p className={cn('text-sm', sectionEnter)}>
                  {t('A2UI_APPROVAL_TOOL', { defaultValue: 'Tool' })}:{' '}
                  <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">
                    {approval.toolName}
                  </code>
                </p>
              )}
              {revealStep >= 2 && hasToolArgs && (
                <pre className={cn('max-h-40 overflow-auto rounded bg-muted p-2 text-xs', sectionEnter)}>
                  {JSON.stringify(approval.toolArgs, null, 2)}
                </pre>
              )}
              {revealStep >= 3 &&
                active &&
                (isInitiator ? (
                  <div className={cn('flex flex-col gap-3', sectionEnter)}>
                    <Input
                      value={comment}
                      onChange={(event) => setComment(event.target.value)}
                      placeholder={t('A2UI_APPROVAL_COMMENT_PLACEHOLDER', {
                        defaultValue: 'Add a comment (optional)',
                      })}
                    />
                    <div className="flex gap-2">
                      {grantScopes.length === 0 ? (
                        <Button size="sm" onClick={() => onDecision('approved', comment)}>
                          {t('A2UI_APPROVE', { defaultValue: 'Approve' })}
                        </Button>
                      ) : (
                        <div className="flex">
                          <Button
                            size="sm"
                            className={cn(grantScopes.length > 1 && 'rounded-r-none')}
                            onClick={() => onDecision('approved', comment, grantScopes[0])}
                          >
                            {grantScopeLabel(t, grantScopes[0], approval)}
                          </Button>
                          {grantScopes.length > 1 && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  size="sm"
                                  className="rounded-l-none border-l border-primary-foreground/25 px-2"
                                  aria-label={t('A2UI_MORE_APPROVAL_OPTIONS', 'More approval options')}
                                >
                                  <NavArrowDown width={14} height={14} strokeWidth={2} aria-hidden />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="start">
                                {grantScopes.slice(1).map((scope) => (
                                  <DropdownMenuItem
                                    key={scope}
                                    onSelect={() => onDecision('approved', comment, scope)}
                                  >
                                    <span>{grantScopeLabel(t, scope, approval)}</span>
                                  </DropdownMenuItem>
                                ))}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onDecision('declined', comment)}
                      >
                        {t('A2UI_DECLINE', { defaultValue: 'Decline' })}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className={cn('text-xs text-muted-foreground', sectionEnter)}>
                    {initiatorName
                      ? t('A2UI_APPROVAL_WAITING_FOR', {
                          defaultValue: 'Waiting for {{name}}',
                          name: initiatorName,
                        })
                      : t('A2UI_APPROVAL_WAITING', {
                          defaultValue: 'Waiting for approval',
                        })}
                  </p>
                ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

interface MessageA2uiSurfacesProps {
  surfaces: MessageA2uiSurface[];
  /** Defaults to the active conversation (main/group chat); preview passes its own. */
  conversationId?: string | null;
  /** Originating user message id (resume `messageId`). */
  userMessageId?: string;
  /** Generation id hosting the surfaces (resume `assistantMessageId`). */
  assistantMessageId?: string;
  /** Host message is the latest agent message; stale active selection surfaces render skipped. */
  isLatest?: boolean;
}

/** Shared render + interaction wiring for a generation's A2UI surfaces (all chat variants). */
export const MessageA2uiSurfaces = ({
  surfaces,
  conversationId,
  userMessageId,
  assistantMessageId,
  isLatest = true,
}: MessageA2uiSurfacesProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { configurationSettings } = useConfig();
  const activeConversationId = useChatStore((state) => state.activeConversationId);
  const cid = conversationId ?? activeConversationId;
  const currentUserId = useCommonStore((state) => state.user?.id);
  const participants = useChatStore((state) =>
    cid ? state.conversations[cid]?.participants : undefined
  );

  const messages = useChatStore((state) => (cid ? state.conversations[cid]?.messages : undefined));
  const pending = useRef(new Set<string>());
  const access = (surface: MessageA2uiSurface) => {
    const originIndex =
      messages?.findIndex(
        (m) => m.recordId === userMessageId || m.generations.some((g) => g.id === userMessageId)
      ) ?? -1;
    const origin = originIndex >= 0 ? messages?.[originIndex] : undefined;
    const owner = surface.initiatorUserId ?? origin?.senderId;
    const laterOwnMessage =
      originIndex >= 0 &&
      messages
        ?.slice(originIndex + 1)
        .some((m) => m.role === 'user' && m.senderId === owner && m.deliveryStatus !== 'failed');
    const latest = originIndex >= 0 ? !laterOwnMessage : isLatest;
    // Unknown ownership in a group fails closed; historical single-user chats remain usable.
    const canRespond = owner ? owner === currentUserId : !(participants && participants.length > 1);
    return { latest, canRespond };
  };

  const submit = (surface: MessageA2uiSurface) => async (action: A2uiAction) => {
    const { latest, canRespond } = access(surface);
    if (
      !cid ||
      !configurationSettings ||
      surface.status !== 'active' ||
      !latest ||
      !canRespond ||
      pending.current.has(surface.surfaceId)
    )
      return;
    const { content, metadata } = buildA2uiSubmit(surface, action);
    const store = useChatStore.getState();
    const conv = store.conversations[cid];
    pending.current.add(surface.surfaceId);
    store.markA2uiSurfaceStatus(cid, surface.surfaceId, 'answered');
    try {
      await store.sendMessage(
        content,
        configurationSettings,
        cid,
        undefined,
        undefined,
        conv?.isGroupChat ?? false,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        conv?.agentId,
        undefined,
        { ...metadata, assistantMessageId }
      );
    } catch {
      store.markA2uiSurfaceStatus(cid, surface.surfaceId, 'active');
      toast({
        variant: 'destructive',
        title: t('A2UI_SUBMIT_FAILED', { defaultValue: 'Could not submit. Please try again.' }),
      });
    } finally {
      pending.current.delete(surface.surfaceId);
    }
  };

  const decide = async (
    surface: MessageA2uiSurface,
    decision: Decision,
    comment: string,
    scope?: ApprovalGrantScope
  ) => {
    const approval = surface.approval;
    if (!cid || !configurationSettings || !approval) return;
    if (resolveApprovalCardStatus(surface) !== 'active') return;
    const store = useChatStore.getState();
    store.markA2uiSurfaceStatus(cid, surface.surfaceId, decision);
    try {
      await getResumeChatStream(
        {
          conversationId: cid,
          messageId: userMessageId ?? '',
          assistantMessageId: assistantMessageId ?? '',
          approvalId: approval.approvalId,
          decision,
          ...(comment.trim() && { comment: comment.trim() }),
          ...(decision === 'approved' && scope ? grantPayload(scope) : {}),
        },
        configurationSettings
      );
    } catch (error) {
      store.markA2uiSurfaceStatus(cid, surface.surfaceId, 'active');
      const forbidden = error instanceof HttpError && error.status === 403;
      toast({
        variant: 'destructive',
        title: forbidden
          ? t('A2UI_APPROVAL_NOT_INITIATOR', {
              defaultValue: 'Only the requester can respond to this approval',
            })
          : t('A2UI_APPROVAL_FAILED', {
              defaultValue: 'Failed to submit approval decision',
            }),
      });
    }
  };

  return (
    <>
      {surfaces.map((surface) => {
        if (surface.approval) {
          const approvalPending = resolveApprovalCardStatus(surface) === 'active';
          return (
            <A2uiApprovalCard
              key={surface.surfaceId}
              surface={surface}
              currentUserId={currentUserId}
              initiatorName={participantName(participants, surface.approval.initiatorUserId)}
              isLatest={approvalPending}
              onDecision={(decision, comment, scope) =>
                void decide(surface, decision, comment, scope)
              }
              // Defensive: an expired approval means the run is over; unstick the
              // composer if END_OF_STREAM never reset it. Only the pending card
              // may reset generating — a historic expired card must not.
              onExpired={
                approvalPending && cid
                  ? () => useChatStore.getState().clearConversationGenerating(cid)
                  : undefined
              }
            />
          );
        }
        const { latest, canRespond } = access(surface);
        const status = deriveStatus(surface, latest);
        return (
          <div key={surface.surfaceId} className="flex flex-col items-start">
            <A2UISurface
              surface={status === surface.status ? surface : { ...surface, status }}
              readOnly={isInteractiveA2uiSurface(surface) && !canRespond}
              onAction={submit(surface)}
            />
            {status !== 'active' && (
              <Badge variant="outline" className="mb-2 text-muted-foreground">
                {statusLabel(t, status)}
              </Badge>
            )}
          </div>
        );
      })}
    </>
  );
};
